import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  query,
  startAfter,
  where,
  doc,
  getDoc,
  QueryDocumentSnapshot,
  DocumentData,
  QueryConstraint,
  orderBy,
  documentId,
} from "firebase/firestore";
import { db, auth } from "./firebase";

export interface QuestionOption {
  id: string;
  text: string;
}

export interface Question {
  id: string;
  subject: string;
  topic: string;
  year: number;
  banca: string;
  institution: string;
  role?: string;
  text: string;
  options: QuestionOption[];
  correctOptionId?: string;
  comments?: string;
  source?: string;
  sourceId?: string;
}

export interface QuestionFilters {
  subject?: string;
  topic?: string;
  banca?: string;
  year?: string; // suporta múltiplos anos separados por vírgula
  institution?: string;
  role?: string;
  status?: "Todas" | "answered" | "unanswered" | "correct" | "incorrect";
  keyword?: string; // Search filter support
}

export interface PaginatedResult {
  questions: Question[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  totalCount: number;
}

export interface FilterOptions {
  subjects: string[];
  bancas: string[];
  years: string[];
  topics: { [subject: string]: string[] };
  institutions: string[];
  roles?: string[];
}

const QUESTIONS_COLLECTION = "questoes";
const fallbackLetters = ["A", "B", "C", "D", "E", "F", "G"];

function normalizeText(x: unknown): string {
  return typeof x === "string" ? x.trim() : String(x ?? "").trim();
}

function normalizeBancaName(value: unknown): string {
  const raw = normalizeText(value);
  if (!raw) return "N/A";
  const cleaned = raw.includes(" - ") ? raw.split(" - ")[0].trim() : raw;
  const normalized = cleaned.toLowerCase();

  if (/cespe|cebraspe/.test(normalized)) return "CESPE/CEBRASPE";
  if (/\bselecon\b/.test(normalized)) return "SELECON";

  return cleaned;
}

function expandBancaFilters(bancas: string[]): string[] {
  const expanded = new Set<string>();

  bancas.forEach((banca) => {
    const raw = normalizeText(banca);
    if (!raw) return;

    expanded.add(raw);
    const canonical = normalizeBancaName(raw);
    expanded.add(canonical);

    if (canonical === "SELECON") {
      [
        "SELECON",
        "Selecon",
        "INSTITUTO SELECON",
        "Instituto Selecon",
        "INSTITUTO SELECON - RJ",
        "Instituto SELECON",
      ].forEach((alias) => expanded.add(alias));
    }
  });

  return Array.from(expanded).filter((b) => b && b !== "Todas").slice(0, 5);
}

function decodeHtmlEntities(text: string = ""): string {
  return text
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&#x2F;/gi, "/");
}

function normalizeLetterId(x: unknown): string | undefined {
  const s = normalizeText(x);
  if (!s) return undefined;
  const m = s.match(/^([A-G])/i);
  return (m ? m[1] : s[0]).toUpperCase();
}

function parseOption(alt: unknown, index: number): QuestionOption {
  if (alt && typeof alt === "object") {
    const anyAlt = alt as any;
    const id =
      normalizeLetterId(anyAlt.id ?? anyAlt.letra ?? anyAlt.optionId) ||
      fallbackLetters[index] ||
      String(index + 1);

    const rawText =
      anyAlt.text ??
      anyAlt.texto ??
      anyAlt.description ??
      anyAlt.descricao ??
      anyAlt.value ??
      "";

    if (rawText) {
      return {
        id,
        text: sanitizeRichText(normalizeText(rawText)),
      };
    }
  }

  const raw = sanitizeRichText(normalizeText(alt));
  const m = raw.match(/^([A-G])\s*[\)\-\.]\s*([\s\S]+)$/);
  if (m) {
    return { id: m[1].toUpperCase(), text: m[2].trim() };
  }
  const letter = fallbackLetters[index] || String(index + 1);
  return { id: letter, text: raw.replace(/^[A-G]\s+/, "").trim() };
}

const stripHtmlImages = (html: string = "") => html.replace(/<img[^>]*>/gi, "");
const stripHtmlTags = (html: string = "") => html.replace(/<[^>]+>/g, "");
const sanitizeRichText = (text: string = "") =>
  decodeHtmlEntities(stripHtmlTags(stripHtmlImages(text))).replace(/\s+/g, " ").trim();

function mapFirestoreToQuestion(docId: string, data: any): Question {
  const alternativas = Array.isArray(data?.alternativas)
    ? data.alternativas
    : Array.isArray(data?.options)
      ? data.options
      : [
          data?.alternativaA,
          data?.alternativaB,
          data?.alternativaC,
          data?.alternativaD,
          data?.alternativaE,
          data?.a,
          data?.b,
          data?.c,
          data?.d,
          data?.e,
        ].filter(Boolean);
  const rawComentarios =
    data?.comentarios ??
    data?.comentario ??
    data?.explicacao ??
    data?.explicação;

  let comments: string | undefined;
  if (typeof rawComentarios === "string") {
    const s = sanitizeRichText(rawComentarios.trim());
    comments = s ? s : undefined;
  } else if (Array.isArray(rawComentarios)) {
    const parts = rawComentarios
      .map((x: any) => sanitizeRichText(normalizeText(x)))
      .filter(Boolean);
    comments = parts.length ? parts.join("\n\n") : undefined;
  }

  const source =
    typeof data?.origem === "string"
      ? data.origem.trim() || undefined
      : undefined;
  const correctOptionId = normalizeLetterId(data?.gabarito ?? data?.correta);

  let year = Number(data?.ano || data?.year);
  if (isNaN(year) || year < 1990 || year > 2026) {
    year = 2025;
  }

  const banca = normalizeBancaName(data?.banca);

  const institutionRaw = normalizeText(data?.orgao || data?.concurso || data?.institution);

  const rawText = normalizeText(data?.enunciado || data?.text);
  const cleanText = sanitizeRichText(rawText);

  return {
    id: docId,
    sourceId: normalizeText(data?.id) || undefined,
    subject: normalizeText(data?.materia || data?.subject) || "Geral",
    topic: normalizeText(data?.assunto || data?.topic) || "Geral",
    year: year,
    banca,
    institution: institutionRaw || "N/A",
    role: normalizeText(data?.cargo) || undefined,
    text: cleanText,
    options: alternativas.map((alt: unknown, idx: number) =>
      parseOption(alt, idx),
    ),
    correctOptionId,
    comments,
    source,
  };
}

// Cache para opções de filtro
let filterOptionsCache: FilterOptions | null = null;
let filterOptionsCacheTime: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

const normalizeSubject = (subject: string = "") =>
  subject
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim();

const BANNED_SUBJECT_PATTERNS = [/desatualizados/i, /manuten[cç][aã]o/i, /^\W+$/];
const BANNED_SUBJECT_SUBSTRINGS = ["manuten", "desatualizado", "manutencao"];
const isBannedSubject = (subject: string = "") => {
  const normalized = normalizeSubject(subject);
  return (
    BANNED_SUBJECT_PATTERNS.some((p) => p.test(subject || "")) ||
    BANNED_SUBJECT_SUBSTRINGS.some((s) => normalized.includes(s))
  );
};

function sanitizeOptions(options: FilterOptions): FilterOptions {
  const subjects = options.subjects.filter((s) =>
    BANNED_SUBJECT_PATTERNS.every((p) => !p.test(s || "")),
  );

  const topics: { [subject: string]: string[] } = {};
  Object.entries(options.topics || {}).forEach(([subject, t]) => {
    if (BANNED_SUBJECT_PATTERNS.every((p) => !p.test(subject || ""))) {
      topics[subject] = t;
    }
  });

  const roles = Array.isArray(options.roles)
    ? options.roles.filter(Boolean)
    : undefined;

  const institutions = Array.isArray(options.institutions)
    ? options.institutions.filter(Boolean)
    : [];

  return {
    ...options,
    subjects,
    topics,
    roles,
    institutions,
  };
}

const buildFallbackYears = () => {
  const fallbackYears: string[] = [];
  for (let y = 2026; y >= 1995; y--) fallbackYears.push(String(y));
  return fallbackYears;
};

/**
 * Busca todas as opções de filtro disponíveis no banco
 * Otimizado para bases grandes: tenta metadata/filterOptions; fallback com amostra limitada.
 */
export async function getFilterOptions(): Promise<FilterOptions> {
  const now = Date.now();

  // Retorna cache se ainda válido E se não contiver itens banidos ou dados faltantes
  if (filterOptionsCache && now - filterOptionsCacheTime < CACHE_DURATION) {
    const hasBannedItems = filterOptionsCache.subjects.some((s) =>
      BANNED_SUBJECT_PATTERNS.some((p) => p.test(s || "")),
    );
    const missingYears = !filterOptionsCache.years || filterOptionsCache.years.length === 0;
    if (!hasBannedItems && !missingYears) {
      return filterOptionsCache;
    }
    console.log("Cache de filtros inválido (banido ou faltando anos). Recarregando...");
    filterOptionsCache = null;
  }

  // 1) Tenta ler metadata pré-calculada
  try {
    const metaRef = doc(db, "metadata", "filterOptions");
    const metaSnap = await getDoc(metaRef);
    if (metaSnap.exists()) {
      const data = metaSnap.data() as any;
      const fromMeta: FilterOptions = sanitizeOptions({
        subjects: Array.isArray(data?.subjects)
          ? Array.from(new Set((data.subjects as unknown[]).map((s) => normalizeText(s)).filter(Boolean)))
          : [],
        bancas: Array.isArray(data?.bancas)
          ? Array.from(new Set((data.bancas as unknown[]).map((b) => normalizeBancaName(b)).filter(Boolean)))
          : [],
        years: Array.isArray(data?.years)
          ? Array.from(new Set((data.years as unknown[]).map((y) => String(y).trim()).filter(Boolean)))
          : [],
        topics:
          typeof data?.topics === "object" && data?.topics !== null
            ? data.topics
            : {},
        institutions: Array.isArray(data?.institutions)
          ? Array.from(new Set((data.institutions as unknown[]).map((i) => normalizeText(i)).filter(Boolean)))
          : [],
        roles: Array.isArray(data?.roles)
          ? Array.from(new Set((data.roles as unknown[]).map((r) => normalizeText(r)).filter(Boolean)))
          : undefined,
      });

      // Se o metadata veio vazio ou incompleto, não cacheia e segue para o fallback
      const hasMetaData =
        fromMeta.subjects.length > 0 ||
        fromMeta.bancas.length > 0 ||
        fromMeta.years.length > 0 ||
        (fromMeta.topics && Object.keys(fromMeta.topics).length > 0) ||
        (fromMeta.roles && fromMeta.roles.length > 0);

      if (hasMetaData) {
        const normalizedYears = Array.isArray(fromMeta.years)
          ? Array.from(
              new Set(
                fromMeta.years
                  .map((y: any) => String(y).trim())
                  .filter((y) => y && y !== "Todos"),
              ),
            ).sort((a, b) => Number(b) - Number(a))
          : [];

        const yearsWithFallback =
          normalizedYears.length > 0 ? normalizedYears : buildFallbackYears();

        if (normalizedYears.length === 0) {
          console.warn(
            "metadata/filterOptions sem anos; aplicando fallback 2026-1995",
          );
        }

        const resolvedMeta = {
          ...fromMeta,
          years: yearsWithFallback,
        };

        filterOptionsCache = resolvedMeta;
        filterOptionsCacheTime = now;
        return resolvedMeta;
      }
    }
  } catch (err) {
    console.warn(
      "metadata/filterOptions ausente ou inválido, usando fallback limitado",
      err,
    );
  }

  // 2) Fallback: paginado em lotes (não varre tudo, mas cobre muito mais que 5k)
  const colRef = collection(db, QUESTIONS_COLLECTION);
  const BATCH_SIZE = 1000;
  const MAX_DOCS = 50000; // limite de segurança
  let processed = 0;
  let lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;
  let stagnantBatches = 0;

  const subjects = new Set<string>();
  const bancas = new Set<string>();
  const MUST_HAVE_BANCAS = [
    "CESPE/CEBRASPE",
    "FGV",
    "FCC",
    "VUNESP",
    "SELECON",
  ];
  const years = new Set<string>();
  const roles = new Set<string>();
  const institutions = new Set<string>();
  const topicsBySubject: { [subject: string]: Set<string> } = {};

  while (processed < MAX_DOCS) {
    let q = query(colRef, orderBy(documentId()), limit(BATCH_SIZE));
    if (lastDoc) {
      q = query(
        colRef,
        orderBy(documentId()),
        startAfter(lastDoc),
        limit(BATCH_SIZE),
      );
    }

    const snap = await getDocs(q);
    if (snap.empty) break;

    const before = subjects.size + bancas.size + years.size;

    snap.docs.forEach((d) => {
      const data = d.data();
      const subject = normalizeText(data?.materia) || "Geral";
      const banca = normalizeBancaName(data?.banca);
      const yearValue = data?.ano || data?.year;
      const year = Number(yearValue);
      const topic = normalizeText(data?.assunto) || "Geral";
      const role = normalizeText(data?.cargo);
      const institution =
        normalizeText(data?.orgao || data?.concurso || data?.institution);

      if (isBannedSubject(subject)) return;

      if (subject && subject !== "Geral") subjects.add(subject);
      if (banca && banca !== "N/A") bancas.add(banca);
      if (!isNaN(year) && year > 1990 && year <= 2026) {
        years.add(String(year));
      }

      if (role) roles.add(role);
      if (institution && institution !== "N/A") institutions.add(institution);

      if (!topicsBySubject[subject]) {
        topicsBySubject[subject] = new Set();
      }
      if (topic && topic !== "Geral") {
        topicsBySubject[subject].add(topic);
      }
    });

    processed += snap.size;
    lastDoc = snap.docs[snap.docs.length - 1];

    const after = subjects.size + bancas.size + years.size;
    stagnantBatches = after === before ? stagnantBatches + 1 : 0;
    // Se não aparecer nada novo por 3 batches seguidos, paramos cedo
    if (stagnantBatches >= 3) break;
  }

  const topics: { [subject: string]: string[] } = {};
  Object.keys(topicsBySubject).forEach((subject) => {
    topics[subject] = Array.from(topicsBySubject[subject]).sort();
  });

  MUST_HAVE_BANCAS.forEach((b) => bancas.add(b));

  filterOptionsCache = sanitizeOptions({
    subjects: Array.from(subjects).sort(),
    bancas: Array.from(bancas).sort(),
    years: Array.from(years).sort((a, b) => Number(b) - Number(a)),
    topics,
    institutions: Array.from(institutions).sort(),
    roles: Array.from(roles).sort(),
  });

  // 3) Último recurso: Se ainda assim não tiver matérias, usa lista estática de emergência
  if (filterOptionsCache.subjects.length === 0) {
    console.warn("Fallback retornou 0 matérias. Usando lista estática.");
    const fallbackSubjects = [
      "Língua Portuguesa",
      "Raciocínio Lógico",
      "Informática",
      "Direito Constitucional",
      "Direito Administrativo",
      "Direito Penal",
      "Direito Processual Penal",
      "Direito Civil",
      "Direito Processual Civil",
      "Matemática",
      "Atualidades",
      "Conhecimentos Bancários",
    ];
    filterOptionsCache.subjects = fallbackSubjects.sort();
  }

  // 4) Fallback de anos se vier vazio (ex.: metadata sem years ou varredura limitada)
  if (!filterOptionsCache.years || filterOptionsCache.years.length === 0) {
    console.warn("Fallback de anos aplicado. Gerando range 2026-1995.");
    filterOptionsCache.years = buildFallbackYears();
  }

  filterOptionsCacheTime = now;

  return filterOptionsCache;
}

/**
 * Conta o total de questões real no servidor usando contagem otimizada (Custo baixíssimo)
 * Se houver filtros de status, retorna -1 para indicar que a contagem deve ser local
 */
export async function getQuestionsCount(
  filters?: QuestionFilters,
  fallbackApplied: boolean = false,
): Promise<number> {
  const colRef = collection(db, QUESTIONS_COLLECTION);

  if (filters) {
    // Se tiver filtro de status, não podemos contar no servidor (regra de negócio local)
    if (filters.status && filters.status !== "Todas") {
      return -1;
    }
  }

  const yearsList = filters?.year && filters.year !== "Todos"
    ? Array.from(new Set(filters.year.split(",").map((y) => y.trim()).filter(Boolean)))
    : [];
  const subjectList = filters?.subject && filters.subject !== "Todas"
    ? Array.from(new Set(filters.subject.split(",").map((s) => s.trim()).filter(Boolean))).slice(0, 5)
    : [];
  const bancaList = filters?.banca && filters.banca !== "Todas"
    ? expandBancaFilters(Array.from(new Set(filters.banca.split(",").map((s) => s.trim()).filter(Boolean))))
    : [];

  // Se houver múltiplos de qualquer dimensão, soma por combinação
  const multiYears = yearsList.length > 1;
  const multiSubjects = subjectList.length > 1;
  const multiBancas = bancaList.length > 1;

  if (multiYears || multiSubjects || multiBancas) {
    const yearsToQuery = (multiYears ? yearsList : yearsList.length === 1 ? yearsList : [null])
      .slice(0, 10)
      .map((y) => (y ? Number(y) : null))
      .filter((n) => n === null || !isNaN(n));
    const subjectsToQuery = (multiSubjects ? subjectList : subjectList.length === 1 ? subjectList : [null]).slice(0, 5);
    const bancasToQuery = (multiBancas ? bancaList : bancaList.length === 1 ? bancaList : [null]).slice(0, 5);

    let total = 0;
    for (const s of subjectsToQuery) {
      for (const b of bancasToQuery) {
        for (const y of yearsToQuery) {
          const constraints: QueryConstraint[] = [];
          if (s) constraints.push(where("materia", "==", s));
          if (!multiSubjects && filters?.topic && filters.topic !== "Todos") constraints.push(where("assunto", "==", filters.topic));
          if (b) constraints.push(where("banca", "==", b));
          if (filters?.institution && filters.institution !== "Todos") constraints.push(where("institution", "==", filters.institution));
          if (filters?.role && filters.role !== "Todos") constraints.push(where("role", "==", filters.role));
          if (y !== null) constraints.push(where("ano", "==", y));
          const q = query(colRef, ...constraints);
          try {
            const snap = await getCountFromServer(q);
            total += snap.data().count;
          } catch (err) {
            console.error("Erro ao contar combinação", { s, b, y }, err);
          }
        }
      }
    }

    // Fallback ±5 anos se zero
    if (total === 0 && !fallbackApplied && yearsToQuery.some((y) => y !== null)) {
      const expanded = new Set<number>();
      yearsToQuery.filter((y) => y !== null).forEach((y: any) => { for (let d = -5; d <= 5; d++) expanded.add(y + d); });
      const fallbackYears = Array.from(expanded)
        .filter((y) => y > 1990 && y <= 2100)
        .sort((a, b) => b - a)
        .slice(0, 10);
      if (fallbackYears.length > 0) {
        return getQuestionsCount({ ...filters, year: fallbackYears.join(",") }, true);
      }
    }
    return total;
  }

  const constraints: QueryConstraint[] = [];
  if (subjectList.length === 1) {
    constraints.push(where("materia", "==", subjectList[0]));
  }
  if (filters?.topic && filters.topic !== "Todos" && subjectList.length <= 1) {
    constraints.push(where("assunto", "==", filters.topic));
  }
  if (bancaList.length === 1) {
    constraints.push(where("banca", "==", bancaList[0]));
  }
  if (yearsList.length === 1) {
    constraints.push(where("ano", "==", Number(yearsList[0])));
  }
  if (filters?.institution && filters.institution !== "Todos") {
    constraints.push(where("institution", "==", filters.institution));
  }
  if (filters?.role && filters.role !== "Todos") {
    constraints.push(where("role", "==", filters.role));
  }

  const q =
    constraints.length > 0 ? query(colRef, ...constraints) : query(colRef);

  try {
    const snapshot = await getCountFromServer(q);
    const count = snapshot.data().count;
    if (count === 0 && yearsList.length === 1 && !fallbackApplied) {
      const y = Number(yearsList[0]);
      if (!isNaN(y)) {
        const expanded: number[] = [];
        for (let d = -5; d <= 5; d++) expanded.push(y + d);
        const fallbackYears = expanded
          .filter((yy) => yy > 1990 && yy <= 2100)
          .sort((a, b) => b - a)
          .slice(0, 10);
        if (fallbackYears.length > 0) {
          return getQuestionsCount({ ...filters, year: fallbackYears.join(",") }, true);
        }
      }
    }
    return count;
  } catch (error) {
    console.error("Erro ao contar questões:", error);
    return 0;
  }
}

/**
 * Busca questões paginadas com filtros
 */
export async function getQuestionsPage(
  pageSize: number,
  cursor: QueryDocumentSnapshot<DocumentData> | null,
  filters?: QuestionFilters,
  fallbackApplied: boolean = false,
): Promise<PaginatedResult> {
  const colRef = collection(db, QUESTIONS_COLLECTION);
  const baseConstraints: QueryConstraint[] = [];

  // Aplicar filtros (sem ano ainda)
  const subjectList = filters?.subject && filters.subject !== "Todas"
    ? Array.from(new Set(filters.subject.split(",").map((s) => s.trim()).filter(Boolean))).slice(0, 5)
    : [];
  const bancaList = filters?.banca && filters.banca !== "Todas"
    ? expandBancaFilters(Array.from(new Set(filters.banca.split(",").map((s) => s.trim()).filter(Boolean))))
    : [];

  // Aplicar filtros base (que não são multi)
  if (subjectList.length === 1) {
    baseConstraints.push(where("materia", "==", subjectList[0]));
  }
  if (filters?.topic && filters.topic !== "Todos" && subjectList.length <= 1) {
    baseConstraints.push(where("assunto", "==", filters.topic));
  }
  if (bancaList.length === 1) {
    baseConstraints.push(where("banca", "==", bancaList[0]));
  }
  if (filters?.institution && filters.institution !== "Todos") {
    baseConstraints.push(where("institution", "==", filters.institution));
  }
  if (filters?.role && filters.role !== "Todos") {
    baseConstraints.push(where("role", "==", filters.role));
  }

  const yearsList = filters?.year && filters.year !== "Todos"
    ? Array.from(new Set(filters.year.split(",").map((y) => y.trim()).filter(Boolean)))
    : [];

  // Quando há múltiplos de qualquer dimensão (anos/subjects/bancas), faz merge manual
  const multiYears = yearsList.length > 1;
  const multiSubjects = subjectList.length > 1;
  const multiBancas = bancaList.length > 1;

  if (multiYears || multiSubjects || multiBancas) {
    const yearsToQuery = (multiYears ? yearsList : yearsList.length === 1 ? yearsList : [null])
      .slice(0, 10)
      .map((y) => (y ? Number(y) : null))
      .filter((n) => n === null || !isNaN(n));
    const subjectsToQuery = (multiSubjects ? subjectList : subjectList.length === 1 ? subjectList : [null]).slice(0, 5);
    const bancasToQuery = (multiBancas ? bancaList : bancaList.length === 1 ? bancaList : [null]).slice(0, 5);

    const collected: any[] = [];
    for (const s of subjectsToQuery) {
      for (const b of bancasToQuery) {
        for (const y of yearsToQuery) {
          const parts: QueryConstraint[] = [...baseConstraints];
          if (s) parts.push(where("materia", "==", s));
          if (!multiSubjects && filters?.topic && filters.topic !== "Todos") {
            parts.push(where("assunto", "==", filters.topic));
          }
          if (b) parts.push(where("banca", "==", b));
          if (y !== null) parts.push(where("ano", "==", y));
          const q = query(colRef, ...parts, limit(pageSize * 2));
          const snap = await getDocs(q);
          collected.push(...snap.docs);
        }
      }
    }

    const unique = new Map<string, any>();
    collected.forEach((d) => unique.set(d.id, d));
    const docs = Array.from(unique.values()).sort((a, b) => (b.data()?.ano || 0) - (a.data()?.ano || 0));

    const syntheticOffset =
      cursor && typeof (cursor as any).__offset === "number"
        ? Math.max(0, Number((cursor as any).__offset) || 0)
        : 0;

    const paged = docs.slice(syntheticOffset, syntheticOffset + pageSize);

    let questions = paged
      .map((d: any) => mapFirestoreToQuestion(d.id, d.data()))
      .filter((q: any) => !isBannedSubject(q.subject));

    // Fallback ±5 anos se continuar vazio e ainda não aplicou
    if (questions.length === 0 && !fallbackApplied && yearsToQuery.some((y) => y !== null)) {
      const expanded = new Set<number>();
      yearsToQuery.filter((y) => y !== null).forEach((y: any) => {
        for (let delta = -5; delta <= 5; delta++) expanded.add(y + delta);
      });
      const fallbackYears = Array.from(expanded)
        .filter((y) => y > 1990 && y <= 2100)
        .sort((a, b) => b - a)
        .slice(0, 10);
      if (fallbackYears.length > 0) {
        return getQuestionsPage(pageSize, cursor, { ...filters, year: fallbackYears.join(",") }, true);
      }
    }

    const totalCount = await getQuestionsCount(filters);
    const hasMore = docs.length > syntheticOffset + pageSize;
    const nextCursor = hasMore
      ? (({ __offset: syntheticOffset + pageSize } as unknown) as QueryDocumentSnapshot<DocumentData>)
      : null;
    return { questions, cursor: nextCursor, hasMore, totalCount };
  }

  // Fluxo padrão (0 ou 1 ano, 0 ou 1 subject/banca)
  const constraints: QueryConstraint[] = [...baseConstraints];
  if (yearsList.length === 1) {
    constraints.push(where("ano", "==", Number(yearsList[0])));
  }

  // Paginação
  if (cursor) {
    constraints.push(startAfter(cursor));
  }
  constraints.push(limit(pageSize));

  const q = query(colRef, ...constraints);
  const snap = await getDocs(q);
  let docs = snap.docs;

  // Fallback: se veio vazio e há filtro de ano, amplia ±5 anos
  if (docs.length === 0 && yearsList.length === 1 && !fallbackApplied) {
    const y = Number(yearsList[0]);
    if (!isNaN(y)) {
      const expanded = [] as number[];
      for (let delta = -5; delta <= 5; delta++) expanded.push(y + delta);
      const fallbackYears = expanded
        .filter((yy) => yy > 1990 && yy <= 2100)
        .sort((a, b) => b - a)
        .slice(0, 10);
      if (fallbackYears.length > 0) {
        return getQuestionsPage(pageSize, cursor, { ...filters, year: fallbackYears.join(",") }, true);
      }
    }
  }

  let questions = docs
    .map((d) => mapFirestoreToQuestion(d.id, d.data()))
    .filter((q) => !isBannedSubject(q.subject));

  // Filtros client-side (Tópico e Status)
  if (filters?.topic && filters.topic !== "Todos") {
    questions = questions.filter((q) => q.topic === filters.topic);
  }

  // Filtro de Status (Respondidas/Não Respondidas)
  if (filters?.status && filters.status !== "Todas" && auth.currentUser) {
    try {
      const userProgressRef = doc(db, "userProgress", auth.currentUser.uid);
      const userDoc = await getDoc(userProgressRef);
      if (userDoc.exists()) {
        const history = userDoc.data().history || [];
        const answeredIds = new Set(history.map((h: any) => h.questionId));

        if (filters.status === "answered") {
          questions = questions.filter((q) => answeredIds.has(q.id));
        } else if (filters.status === "unanswered") {
          questions = questions.filter((q) => !answeredIds.has(q.id));
        } else if (filters.status === "correct") {
          const correctIds = new Set(
            history
              .filter((h: any) => h.isCorrect)
              .map((h: any) => h.questionId),
          );
          questions = questions.filter((q) => correctIds.has(q.id));
        } else if (filters.status === "incorrect") {
          const incorrectIds = new Set(
            history
              .filter((h: any) => !h.isCorrect)
              .map((h: any) => h.questionId),
          );
          questions = questions.filter((q) => incorrectIds.has(q.id));
        }
      }
    } catch (error) {
      console.error("Erro ao filtrar por status:", error);
    }
  }

  const nextCursor = docs.length > 0 ? docs[docs.length - 1] : null;
  const hasMore = docs.length === pageSize;

  // Contar total com os mesmos filtros
  const totalCount = await getQuestionsCount(filters);

  return { questions, cursor: nextCursor, hasMore, totalCount };
}

/**
 * Busca todas as questões de uma vez (para uso em filtros client-side)
 * Limitado a 10000 questões para garantir que traga tudo
 */
export async function getAllQuestions(
  filters?: QuestionFilters,
  maxQuestions: number = 10000,
): Promise<Question[]> {
  const colRef = collection(db, QUESTIONS_COLLECTION);
  const constraints: QueryConstraint[] = [];

  if (filters?.subject && filters.subject !== "Todas") {
    constraints.push(where("materia", "==", filters.subject));
  }
  if (filters?.topic && filters.topic !== "Todos") {
    constraints.push(where("assunto", "==", filters.topic));
  }
  if (filters?.banca && filters.banca !== "Todas") {
    const bancaList = expandBancaFilters(
      Array.from(new Set(filters.banca.split(",").map((s) => s.trim()).filter(Boolean))),
    );
    if (bancaList.length === 1) {
      constraints.push(where("banca", "==", bancaList[0]));
    } else if (bancaList.length > 1) {
      constraints.push(where("banca", "in", bancaList.slice(0, 10)));
    }
  }
  const yearsList = (filters?.year && filters.year !== "Todos")
    ? Array.from(new Set(filters.year.split(",").map((y) => y.trim()).filter(Boolean)))
    : [];
  if (yearsList.length === 1) {
    constraints.push(where("ano", "==", Number(yearsList[0])));
  } else if (yearsList.length > 1) {
    constraints.push(where("ano", "in", yearsList.slice(0, 10).map((y) => Number(y))));
  }
  if (filters?.institution && filters.institution !== "Todos") {
    constraints.push(where("institution", "==", filters.institution));
  }
  if (filters?.role && filters.role !== "Todos") {
    constraints.push(where("role", "==", filters.role));
  }

  constraints.push(limit(maxQuestions));

  const q = query(colRef, ...constraints);
  const snap = await getDocs(q);

  // Note: We are returning all questions, but topic and status filters
  // must be applied by the caller (client-side) because they require logic
  // or fields not indexed the same way.
  return snap.docs
    .map((d) => mapFirestoreToQuestion(d.id, d.data()))
    .filter((q) => !isBannedSubject(q.subject));
}

/**
 * Busca questões com paginação simples (sem filtros server-side)
 * Mantido para compatibilidade
 */
export async function getQuestionsPageSimple(
  pageSize: number,
  cursor: QueryDocumentSnapshot<DocumentData> | null,
): Promise<{
  questions: Question[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}> {
  const colRef = collection(db, QUESTIONS_COLLECTION);

  const q = cursor
    ? query(colRef, startAfter(cursor), limit(pageSize))
    : query(colRef, limit(pageSize));

  const snap = await getDocs(q);
  const docs = snap.docs;

  const questions = docs
    .map((d) => mapFirestoreToQuestion(d.id, d.data()))
    .filter((q) => !isBannedSubject(q.subject));
  const nextCursor = docs.length > 0 ? docs[docs.length - 1] : null;
  const hasMore = docs.length === pageSize;

  return { questions, cursor: nextCursor, hasMore };
}

/**
 * Limpa o cache de filtros (útil após adicionar novas questões)
 */
export function clearFilterCache(): void {
  filterOptionsCache = null;
  filterOptionsCacheTime = 0;
}
